import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Sparkles, CheckCircle2, Clock, IndianRupee, ShieldCheck } from "lucide-react";
import api, { formatINR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSubscription } from "@/context/SubscriptionContext";
import { Button } from "@/components/ui/button";

// Razorpay Checkout script (loaded lazily, cached after first inject).
function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

const FEATURES = [
  "All Schedule III Reports — BS, P&L, Cash Flow with party-wise drill-down",
  "Unlimited Companies, Ledgers, Vouchers",
  "GST: GSTR-1 / GSTR-3B with JSON + Excel export",
  "Banking: Reconciliation + CTS-2010 Cheque Print",
  "Payroll: Employees, Salary Structures, Payslip generation",
  "Inventory: FIFO valuation, Reorder alerts, Bulk upload",
  "Multi-user (when added) — all your data, your hardware",
  "Auto-backup + Restore (JSON snapshot)",
];

export default function Subscribe() {
  const { user } = useAuth();
  const { sub, refresh, isExpired, isTrial, isActive, isExempt, daysRemaining, amountPaise, currency, razorpayKeyId, razorpayConfigured } = useSubscription();
  const [paying, setPaying] = useState(false);
  const nav = useNavigate();

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    if (isExempt) nav("/", { replace: true });
  }, [isExempt, nav]);

  const handlePay = async () => {
    if (!razorpayConfigured) {
      toast.error("Payments are not yet enabled. Please contact support — Razorpay keys haven't been configured on the server.");
      return;
    }
    setPaying(true);
    try {
      const ok = await loadRazorpay();
      if (!ok) { toast.error("Could not load Razorpay Checkout. Check your network."); setPaying(false); return; }
      const { data: order } = await api.post("/subscription/order");
      const options = {
        key: order.key_id || razorpayKeyId,
        amount: order.amount,
        currency: order.currency,
        name: "AXIORA",
        description: "Annual Subscription (₹100 / year)",
        order_id: order.order_id,
        prefill: { name: order.user_name, email: order.user_email },
        theme: { color: "#1d4ed8" },
        handler: async (response) => {
          try {
            const verify = await api.post("/subscription/verify", {
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
            });
            await refresh();
            toast.success(`Subscription activated until ${new Date(verify.data.expires_at).toLocaleDateString("en-IN")}`);
            setTimeout(() => nav("/", { replace: true }), 1500);
          } catch (e) {
            toast.error("Payment verification failed: " + (e?.response?.data?.detail || e.message));
          } finally {
            setPaying(false);
          }
        },
        modal: { ondismiss: () => setPaying(false) },
      };
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (resp) => {
        toast.error("Payment failed: " + (resp?.error?.description || "Unknown error"));
        setPaying(false);
      });
      rzp.open();
    } catch (e) {
      toast.error("Could not start payment: " + (e?.response?.data?.detail || e.message));
      setPaying(false);
    }
  };

  if (!sub) return null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="subscribe-page">
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full mb-4">
            <Sparkles className="w-3.5 h-3.5" /> AXIORA Annual Subscription
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight">
            Unlimited accounting for <span className="text-blue-700">₹100/year</span>
          </h1>
          <p className="text-base text-slate-600 mt-3 max-w-2xl mx-auto">
            That&apos;s <span className="font-semibold">less than ₹9 a month</span> for a complete Schedule III–compliant accounting suite for your business.
          </p>
        </div>

        {/* Current state banner */}
        <div className={`mb-8 p-4 rounded-lg border ${
          isExpired ? "bg-red-50 border-red-200 text-red-900" :
          isTrial ? "bg-amber-50 border-amber-200 text-amber-900" :
          isActive ? "bg-emerald-50 border-emerald-200 text-emerald-900" :
          "bg-slate-50 border-slate-200 text-slate-700"
        }`} data-testid="sub-state-banner">
          <div className="flex items-start gap-3">
            {isExpired ? <Lock className="w-5 h-5 mt-0.5" /> :
             isTrial ? <Clock className="w-5 h-5 mt-0.5" /> :
             <CheckCircle2 className="w-5 h-5 mt-0.5" />}
            <div className="flex-1">
              {isExpired && (
                <>
                  <div className="font-semibold text-sm" data-testid="sub-state-text">Your trial has ended.</div>
                  <div className="text-xs mt-1 opacity-90">All write operations (creating companies, vouchers, ledgers, etc.) are blocked until you subscribe. Your data is safe and will be available immediately on activation.</div>
                </>
              )}
              {isTrial && (
                <>
                  <div className="font-semibold text-sm" data-testid="sub-state-text">You&apos;re on a free 7-day trial.</div>
                  <div className="text-xs mt-1 opacity-90">
                    {daysRemaining > 0 ? <>You have <span className="font-bold">{daysRemaining} day{daysRemaining === 1 ? "" : "s"}</span> remaining.</> : <>Your trial ends today.</>}
                    {" "}Subscribe now to avoid interruption — your trial days are not lost, the ₹100 simply extends from today.
                  </div>
                </>
              )}
              {isActive && (
                <>
                  <div className="font-semibold text-sm" data-testid="sub-state-text">Subscription active</div>
                  <div className="text-xs mt-1 opacity-90">
                    Valid until <span className="font-mono">{new Date(sub.expires_at).toLocaleDateString("en-IN")}</span> ({daysRemaining} days remaining). Renew anytime — payment will extend from current expiry, not from today.
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Plan card */}
        <div className="grid md:grid-cols-5 gap-6">
          <div className="md:col-span-3 bg-white border border-slate-200 rounded-xl p-8 shadow-sm">
            <div className="flex items-baseline gap-1 mb-1">
              <IndianRupee className="w-7 h-7 text-slate-900" />
              <span className="text-5xl font-bold text-slate-900 tracking-tight">{formatINR(amountPaise / 100).replace(".00", "")}</span>
              <span className="text-base text-slate-500 ml-1">/ year</span>
            </div>
            <p className="text-xs text-slate-500 mb-6">One-time annual payment · No auto-debit · No hidden charges · GST as applicable</p>

            <ul className="space-y-2.5">
              {FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-sm text-slate-700">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button
                onClick={handlePay}
                disabled={paying || isExempt}
                className="flex-1 bg-blue-700 hover:bg-blue-800 text-white font-semibold py-6 text-base"
                data-testid="sub-pay-button"
              >
                {paying ? "Opening Razorpay…" : isActive ? `Renew for ₹${amountPaise / 100}` : `Pay ₹${amountPaise / 100} & Activate`}
              </Button>
              {!razorpayConfigured && (
                <span className="text-[11px] text-amber-700 self-center" data-testid="sub-not-configured-msg">
                  Payments not yet enabled — contact support
                </span>
              )}
            </div>
            <div className="mt-4 flex items-center gap-2 text-[11px] text-slate-500">
              <ShieldCheck className="w-3.5 h-3.5" />
              Secured by Razorpay · UPI, Cards, Net Banking, Wallets · 100% Indian payment gateway
            </div>
          </div>

          <div className="md:col-span-2 bg-slate-900 text-slate-100 rounded-xl p-8 shadow-sm">
            <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">Why AXIORA?</div>
            <h3 className="text-xl font-semibold text-white mb-3">Built for Indian SMEs</h3>
            <ul className="space-y-3 text-sm text-slate-300">
              <li><span className="text-white font-semibold">Schedule III compliant</span> — auditor-ready Balance Sheet & P&L, with party-wise ledger drill-down printed inline.</li>
              <li><span className="text-white font-semibold">GST returns built-in</span> — GSTR-1 + GSTR-3B in 1 click, JSON for the portal, Excel for your books.</li>
              <li><span className="text-white font-semibold">No installs</span> — runs in any browser, anywhere. Your books are yours, encrypted and backed up.</li>
              <li><span className="text-white font-semibold">No surprise pricing</span> — flat ₹100/year, full app. Cancel anytime by simply not renewing.</li>
            </ul>
            <div className="mt-6 pt-6 border-t border-slate-700 text-xs text-slate-400">
              Questions? Email <span className="text-white">support@tallyx.com</span>
            </div>
          </div>
        </div>

        {user && (
          <div className="mt-8 text-center text-xs text-slate-500" data-testid="sub-logged-as">
            Logged in as <span className="font-mono text-slate-700">{user.email}</span> ·{" "}
            <button onClick={() => nav("/")} className="text-blue-700 hover:underline" data-testid="sub-back-link">Back to app</button>
          </div>
        )}
      </div>
    </div>
  );
}
